import { useEffect, useState, useMemo } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Edit,
  Trash2,
  CheckCircle2,
  Clock,
  User,
  FileText,
  Calendar,
  Play,
  XCircle,
  Upload,
  ClipboardList,
  FolderOpen,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useActivityStore } from '@/store/activityStore';
import { useActivityTypeStore } from '@/store/activityTypeStore';
import { useAuthStore } from '@/store/authStore';
import { formatItalianDate, formatItalianDateTime } from '@/utils/date';
import { toast } from 'sonner';
import { apiClient } from '@/utils/api';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import ActivityFileDropzone from '@/components/activities/ActivityFileDropzone';
import ActivityFormDialog from '@/components/activities/ActivityFormDialog';
import { Activity } from '@/services/activityService';
import { cn } from '@/lib/utils';

const activityTypeLabels: Record<string, string> = {
  CALL: 'Chiamata',
  EMAIL: 'Email',
  MEETING: 'Riunione',
  DOCUMENT_REVIEW: 'Revisione Documento',
  SITE_VISIT: 'Sopralluogo',
  FILING: 'Archiviazione',
  PAYMENT: 'Pagamento',
  FOLLOW_UP: 'Follow-up',
  OTHER: 'Altro',
};

interface AssignableUser {
  id: string;
  firstName: string;
  lastName: string;
  email?: string;
}

export default function ActivityDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [users, setUsers] = useState<AssignableUser[]>([]);
  const [actionMode, setActionMode] = useState<'file' | 'form' | null>(null);

  const { user: currentUser } = useAuthStore();
  const { activityTypes, fetchActivityTypes } = useActivityTypeStore();

  const {
    currentActivity: activity,
    loading,
    error,
    fetchActivity,
    deleteActivity,
    updateActivity,
    updateActivityStatus,
    clearError,
  } = useActivityStore();

  useEffect(() => {
    fetchActivityTypes(true);
  }, [fetchActivityTypes]);

  useEffect(() => {
    apiClient
      .get('/users/assignable')
      .then((res) => setUsers(res.data?.data || []))
      .catch(() => {
        if (currentUser) {
          setUsers([
            {
              id: currentUser.id,
              firstName: currentUser.firstName || '',
              lastName: currentUser.lastName || '',
              email: currentUser.email,
            },
          ]);
        }
      });
  }, [currentUser]);

  useEffect(() => {
    if (id) {
      fetchActivity(id).catch(() => {
        toast.error("Errore nel caricamento dell'attività");
      });
    }
  }, [id, fetchActivity]);

  useEffect(() => {
    if (error) {
      toast.error(error);
      clearError();
    }
  }, [error, clearError]);

  const handleDelete = async () => {
    if (!id) return;
    try {
      await deleteActivity(id);
      toast.success('Attività eliminata con successo');
      navigate('/activities');
    } catch {
      // Error already handled in store
    }
    setShowDeleteDialog(false);
  };

  const handleStatusChange = async (status: 'PENDING' | 'IN_PROGRESS' | 'COMPLETED' | 'CANCELLED') => {
    if (!id) return;
    try {
      await updateActivityStatus(id, status);
    } catch {
      // Error already handled in store
    }
  };

  const handleAssign = async (userId: string) => {
    if (!id) return;
    try {
      await updateActivity(id, { userId: userId || undefined });
    } catch {
      // Error already handled in store
    }
  };

  const effectiveActionType = useMemo(() => {
    if (!activity) return null;
    const at = activityTypes.find((t) => t.code === activity.type);
    if (at?.actionType) return at.actionType as 'file' | 'form';
    if (at?._count?.modules && at._count.modules > 0) return 'form';
    return 'file';
  }, [activity, activityTypes]);

  if (loading && !activity) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <p className="text-gray-500">Caricamento attività...</p>
      </div>
    );
  }

  if (!activity) {
    return (
      <div className="space-y-6">
        <Button variant="ghost" onClick={() => navigate('/activities')}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Torna alle Attività
        </Button>
        <Card>
          <CardContent className="pt-6">
            <p className="text-center text-gray-500">Attività non trovata</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const isOverdue =
    activity.dueDate && new Date(activity.dueDate) < new Date() && activity.status !== 'COMPLETED';
  const daysUntilDue = activity.dueDate
    ? Math.ceil((new Date(activity.dueDate).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24))
    : null;

  const userOptions = [
    { value: '_none_', label: 'Non assegnata' },
    ...users.map((u) => ({
      value: u.id,
      label: `${u.firstName} ${u.lastName}`,
    })),
  ];

  const showWorkflow = activity.status !== 'COMPLETED' && activity.status !== 'CANCELLED';
  const moduleIdForForm = activityTypes.find((t) => t.code === activity.type)?.moduleId;

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">
        <div className="flex flex-col gap-3 min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <Button variant="ghost" onClick={() => navigate('/activities')}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Torna alle Attività
            </Button>
            {activity.practice?.id && (
              <Button variant="ghost" onClick={() => navigate(`/practices/${activity.practice!.id}`)}>
                <FileText className="h-4 w-4 mr-2" />
                Torna alla Pratica
              </Button>
            )}
            {activity.practice?.id && (
              <Button variant="ghost" asChild>
                <Link to={`/practices/${activity.practice.id}?tab=documents`}>
                  <FolderOpen className="h-4 w-4 mr-2" />
                  Documenti pratica
                </Link>
              </Button>
            )}
          </div>
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 break-words">{activity.title}</h1>
            <div className="flex flex-wrap items-center gap-2 mt-2">
              <Badge
                variant="outline"
                className={
                  activity.status === 'PENDING'
                    ? 'bg-yellow-100 text-yellow-800'
                    : activity.status === 'IN_PROGRESS'
                      ? 'bg-blue-100 text-blue-800'
                      : activity.status === 'COMPLETED'
                        ? 'bg-green-100 text-green-800'
                        : 'bg-gray-100 text-gray-800'
                }
              >
                {activity.status === 'PENDING'
                  ? 'In Attesa'
                  : activity.status === 'IN_PROGRESS'
                    ? 'In Corso'
                    : activity.status === 'COMPLETED'
                      ? 'Completata'
                      : 'Annullata'}
              </Badge>
              <Badge variant="outline">{activityTypeLabels[activity.type] || activity.type}</Badge>
            </div>
          </div>
        </div>

        <div className="flex flex-col gap-3 shrink-0 w-full xl:w-auto xl:max-w-xl">
          <div className="flex flex-wrap items-center gap-2 justify-end">
            {showWorkflow && (
              <>
                {activity.status === 'IN_PROGRESS' && effectiveActionType && (
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    className={cn(
                      'gap-1',
                      effectiveActionType === 'file'
                        ? 'bg-orange-100 text-orange-800 hover:bg-orange-200'
                        : 'bg-purple-100 text-purple-800 hover:bg-purple-200'
                    )}
                    onClick={() => setActionMode(effectiveActionType)}
                  >
                    {effectiveActionType === 'file' ? (
                      <Upload className="h-4 w-4" />
                    ) : (
                      <ClipboardList className="h-4 w-4" />
                    )}
                    {effectiveActionType === 'file' ? 'File' : 'Modulo'}
                  </Button>
                )}
                {activity.status === 'PENDING' && (
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    className="h-8 gap-1 px-2 text-xs bg-blue-100 text-blue-800 hover:bg-blue-200 border-0"
                    onClick={() => handleStatusChange('IN_PROGRESS')}
                    title="Accetta (mette in corso)"
                  >
                    <Play className="h-4 w-4" />
                    Accetta
                  </Button>
                )}
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  className="h-8 gap-1 px-2 text-xs bg-green-100 text-green-800 hover:bg-green-200 border-0"
                  onClick={() => handleStatusChange('COMPLETED')}
                >
                  <CheckCircle2 className="h-4 w-4" />
                  Completa
                </Button>
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  className="h-8 gap-1 px-2 text-xs bg-gray-100 text-gray-700 hover:bg-gray-200 border-0"
                  onClick={() => handleStatusChange('CANCELLED')}
                >
                  <XCircle className="h-4 w-4" />
                  Annulla
                </Button>
              </>
            )}
            {users.length > 0 && showWorkflow && (
              <Select
                value={activity.userId || '_none_'}
                onValueChange={(v) => handleAssign(v === '_none_' ? '' : v)}
              >
                <SelectTrigger className="w-[180px] h-8 text-xs">
                  <SelectValue placeholder="Assegna a..." />
                </SelectTrigger>
                <SelectContent>
                  {userOptions.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-2 justify-end">
            <Button variant="outline" size="sm" onClick={() => navigate(`/activities/${id}/edit`)}>
              <Edit className="h-4 w-4 mr-2" />
              Modifica
            </Button>
            <Button variant="destructive" size="sm" onClick={() => setShowDeleteDialog(true)}>
              <Trash2 className="h-4 w-4 mr-2" />
              Elimina
            </Button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Informazioni Attività</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {activity.description && (
              <div>
                <h3 className="text-sm font-semibold text-gray-700 mb-1">Descrizione</h3>
                <p className="text-gray-600 whitespace-pre-wrap">{activity.description}</p>
              </div>
            )}

            <div>
              <h3 className="text-sm font-semibold text-gray-700 mb-1">Tipo</h3>
              <p className="text-gray-600">{activityTypeLabels[activity.type] || activity.type}</p>
            </div>

            <div>
              <h3 className="text-sm font-semibold text-gray-700 mb-1">Stato</h3>
              <Badge
                variant="outline"
                className={
                  activity.status === 'PENDING'
                    ? 'bg-yellow-100 text-yellow-800'
                    : activity.status === 'IN_PROGRESS'
                      ? 'bg-blue-100 text-blue-800'
                      : activity.status === 'COMPLETED'
                        ? 'bg-green-100 text-green-800'
                        : 'bg-gray-100 text-gray-800'
                }
              >
                {activity.status === 'PENDING'
                  ? 'In Attesa'
                  : activity.status === 'IN_PROGRESS'
                    ? 'In Corso'
                    : activity.status === 'COMPLETED'
                      ? 'Completata'
                      : 'Annullata'}
              </Badge>
            </div>

            {activity.dueDate && (
              <div>
                <h3 className="text-sm font-semibold text-gray-700 mb-1">Scadenza</h3>
                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4 text-gray-500" />
                  <span className={isOverdue ? 'text-red-600 font-semibold' : 'text-gray-600'}>
                    {formatItalianDate(activity.dueDate)}
                    {isOverdue && ` (Scaduta ${Math.abs(daysUntilDue!)} giorni fa)`}
                    {!isOverdue && daysUntilDue === 0 && ' (Scade oggi)'}
                    {!isOverdue && daysUntilDue && daysUntilDue > 0 && ` (Tra ${daysUntilDue} giorni)`}
                  </span>
                </div>
              </div>
            )}

            {activity.completedAt && (
              <div>
                <h3 className="text-sm font-semibold text-gray-700 mb-1">Completata il</h3>
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4 text-green-600" />
                  <span className="text-gray-600">{formatItalianDateTime(activity.completedAt)}</span>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Collegamenti</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {activity.practice && (
              <div>
                <h3 className="text-sm font-semibold text-gray-700 mb-1">Pratica</h3>
                {activity.practice.practiceNumber && (
                  <p className="text-sm text-muted-foreground mb-1">
                    Numero pratica:{' '}
                    <span className="font-mono font-semibold text-foreground">
                      #{activity.practice.practiceNumber}
                    </span>
                  </p>
                )}
                <Link
                  to={`/practices/${activity.practice.id}`}
                  className="text-blue-600 hover:underline flex items-center gap-2"
                >
                  <FileText className="h-4 w-4" />
                  {activity.practice.title}
                </Link>
                <div className="mt-2 flex flex-wrap gap-2">
                  {activity.practice.type && (
                    <Badge variant="outline">
                      {activity.practice.type.name || activity.practice.type.code}
                    </Badge>
                  )}
                  <Button variant="outline" size="sm" asChild>
                    <Link to={`/practices/${activity.practice.id}?tab=documents`}>
                      <FolderOpen className="h-4 w-4 mr-1" />
                      Apri cartella documenti
                    </Link>
                  </Button>
                </div>
              </div>
            )}

            {activity.user && (
              <div>
                <h3 className="text-sm font-semibold text-gray-700 mb-1">Assegnata a</h3>
                <div className="flex items-center gap-2">
                  <User className="h-4 w-4 text-gray-500" />
                  <span className="text-gray-600">
                    {activity.user.firstName} {activity.user.lastName}
                  </span>
                </div>
                {activity.user.email && (
                  <p className="text-sm text-gray-500 mt-1">{activity.user.email}</p>
                )}
              </div>
            )}

            <div>
              <h3 className="text-sm font-semibold text-gray-700 mb-1">Creato</h3>
              <div className="flex items-center gap-2">
                <Clock className="h-4 w-4 text-gray-500" />
                <span className="text-gray-600">{formatItalianDateTime(activity.createdAt)}</span>
              </div>
            </div>

            <div>
              <h3 className="text-sm font-semibold text-gray-700 mb-1">Ultimo aggiornamento</h3>
              <div className="flex items-center gap-2">
                <Clock className="h-4 w-4 text-gray-500" />
                <span className="text-gray-600">{formatItalianDateTime(activity.updatedAt)}</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {actionMode === 'file' && activity && (
        <ActivityFileDropzone
          open={true}
          onClose={() => setActionMode(null)}
          activityTitle={activity.title}
          practiceId={activity.practiceId}
          practiceDocumentPath={activity.practice?.documentPath ?? undefined}
          subfolder={activity.type}
          activityId={activity.id}
          onUploadSuccess={() => {
            if (id) fetchActivity(id);
            setActionMode(null);
          }}
        />
      )}

      {actionMode === 'form' && activity && (
        <ActivityFormDialog
          open={true}
          onClose={() => setActionMode(null)}
          activityTitle={activity.title}
          activityTypeCode={activity.type}
          practiceId={activity.practiceId}
          practiceDocumentPath={activity.practice?.documentPath ?? undefined}
          subfolder={activity.type}
          moduleId={moduleIdForForm}
        />
      )}

      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Elimina Attività</DialogTitle>
            <DialogDescription>
              Sei sicuro di voler eliminare questa attività? Questa azione non può essere annullata.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>
              Annulla
            </Button>
            <Button variant="destructive" onClick={handleDelete}>
              Elimina
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
